import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { createPayHereOrder, submitPayHereCheckout, calculateFinalAmount } from '../lib/payhere';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import TransactionBreakdown from './TransactionBreakdown';
import type { RegistryItem } from '../types/registry';

interface Props {
  item: RegistryItem;
  currency: string;
  onClose: () => void;
  onSuccess: () => void;
  feeResponsibility?: 'COUPLE' | 'GUEST' | 'SPLIT';
  platformFeePercentage?: number;
  gatewayFeePercentage?: number;
}

export default function PaymentModal({
  item,
  currency,
  onClose,
  onSuccess,
  feeResponsibility = 'GUEST',
  platformFeePercentage,
  gatewayFeePercentage
}: Props) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registryDetails, setRegistryDetails] = useState<{
    fee_responsibility: string;
    platform_fee_percentage: number;
    gateway_fee_percentage: number;
    user_id: string;
  } | null>(null);
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: ''
  });

  useEffect(() => {
    loadRegistryDetails();
    if (user) {
      prefillUserInfo();
    }
  }, []);

  const prefillUserInfo = async () => {
    if (!user) return;
    const email = user.email || '';
    const meta = user.user_metadata || {};
    const fullName = (meta.full_name || meta.name || '').trim();
    const parts = fullName.split(' ');
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';

    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', user.id)
      .maybeSingle();

    let profileFirst = firstName;
    let profileLast = lastName;
    if (profile?.full_name) {
      const pParts = profile.full_name.trim().split(' ');
      profileFirst = pParts[0] || firstName;
      profileLast = pParts.slice(1).join(' ') || lastName;
    }

    setUserInfo(prev => ({
      ...prev,
      firstName: prev.firstName || profileFirst,
      lastName: prev.lastName || profileLast,
      email: prev.email || email,
    }));
  };

  const loadRegistryDetails = async () => {
    try {
      const { data: registry, error } = await supabase
        .from('registries')
        .select('fee_responsibility, platform_fee_percentage, gateway_fee_percentage, user_id')
        .eq('id', item.registry_id)
        .single();

      if (error) throw error;
      if (registry) {
        setRegistryDetails(registry);
      }
    } catch (err) {
      console.error('Error loading registry details:', err);
    }
  };

  const effectiveFeeResponsibility = registryDetails?.fee_responsibility || feeResponsibility || 'GUEST';
  const effectivePlatformFee = registryDetails?.platform_fee_percentage || platformFeePercentage || 0;
  const effectiveGatewayFee = registryDetails?.gateway_fee_percentage || gatewayFeePercentage || 0;
  const registryOwnerId = registryDetails?.user_id || '';

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userInfo.firstName || !userInfo.lastName || !userInfo.email || !userInfo.phone || !userInfo.address || !userInfo.city) {
      setError('Please fill in all required fields');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const response = await createPayHereOrder({
        itemId: item.id,
        itemName: item.name,
        originalAmount: item.price,
        currency: currency,
        feeResponsibility: effectiveFeeResponsibility as 'COUPLE' | 'GUEST' | 'SPLIT',
        platformFeePercentage: effectivePlatformFee,
        gatewayFeePercentage: effectiveGatewayFee,
        registryId: item.registry_id,
        registryOwnerId: registryOwnerId,
        payerFirstName: userInfo.firstName,
        payerLastName: userInfo.lastName,
        payerEmail: userInfo.email,
        payerPhone: userInfo.phone,
        payerAddress: userInfo.address,
        payerCity: userInfo.city,
        isMoneyGift: item.is_money_gift || false,
      });

      submitPayHereCheckout({
        merchantId: response.merchantId,
        orderId: response.orderId,
        hash: response.hash,
        itemName: item.name,
        finalAmount: response.finalAmount,
        currency: currency,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
        phone: userInfo.phone,
        address: userInfo.address,
        city: userInfo.city,
        registryId: item.registry_id,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/20 z-[100] flex flex-col justify-end sm:items-center sm:justify-center sm:p-4">
      <div className="absolute inset-0" onClick={onClose} />

      <div className="relative w-full sm:max-w-2xl lg:max-w-4xl bg-white sm:bg-white/80 sm:backdrop-blur-xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[90vh] sm:border sm:border-white/40 sm:shadow-soft-lg">

        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-400" />
        </div>

        <div className="flex items-start justify-between px-5 pt-4 pb-4 sm:px-6 sm:pt-5 border-b border-white/40 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-3">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 leading-snug font-poppins">Claim Gift</h3>
            <p className="mt-1 text-sm text-gray-500 truncate font-poppins">Complete your info to claim {item.name}</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-full text-gray-500 hover:text-gray-800 hover:bg-white/60 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
          {error && (
            <div className="mb-5 bg-red-50 text-red-700 p-4 rounded-lg text-sm font-poppins">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-5">
              <div className="bg-white/60 p-4 rounded-xl">
                <div className="flex justify-between items-center gap-4">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5 font-poppins">Item</p>
                    <p className="text-base font-semibold text-gray-800 truncate font-poppins">{item.name}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5 font-poppins">Price</p>
                    <p className="text-base font-semibold text-gray-800 font-poppins">{currency} {item.price.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handlePayment} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">First Name *</label>
                    <input
                      type="text"
                      value={userInfo.firstName}
                      onChange={(e) => setUserInfo({ ...userInfo, firstName: e.target.value })}
                      className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                      placeholder="First name"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">Last Name *</label>
                    <input
                      type="text"
                      value={userInfo.lastName}
                      onChange={(e) => setUserInfo({ ...userInfo, lastName: e.target.value })}
                      className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                      placeholder="Last name"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">Email *</label>
                  <input
                    type="email"
                    value={userInfo.email}
                    onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                    placeholder="Enter your email address"
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">Phone *</label>
                  <input
                    type="tel"
                    value={userInfo.phone}
                    onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                    placeholder="Enter your phone number"
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">Address *</label>
                  <input
                    type="text"
                    value={userInfo.address}
                    onChange={(e) => setUserInfo({ ...userInfo, address: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                    placeholder="Enter your address"
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">City *</label>
                  <input
                    type="text"
                    value={userInfo.city}
                    onChange={(e) => setUserInfo({ ...userInfo, city: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                    placeholder="Enter your city"
                    required
                    disabled={loading}
                  />
                </div>

                {registryDetails && (
                  <div className="lg:hidden pt-1">
                    <TransactionBreakdown
                      originalAmount={item.price}
                      currency={currency}
                      showDetailed={false}
                      feeResponsibility={effectiveFeeResponsibility}
                      platformFeePercentage={effectivePlatformFee}
                      gatewayFeePercentage={effectiveGatewayFee}
                    />
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 px-4 py-3 text-sm text-gray-600 hover:text-gray-800 bg-white/60 rounded-lg hover:bg-white/80 transition-colors font-poppins"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-3 text-sm bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors font-medium font-poppins"
                  >
                    {loading ? 'Redirecting to PayHere...' : 'Pay with PayHere'}
                  </button>
                </div>
              </form>
            </div>

            {registryDetails && (
              <div className="hidden lg:block space-y-4">
                <TransactionBreakdown
                  originalAmount={item.price}
                  currency={currency}
                  showDetailed={false}
                  feeResponsibility={effectiveFeeResponsibility}
                  platformFeePercentage={effectivePlatformFee}
                  gatewayFeePercentage={effectiveGatewayFee}
                />
                <div className="bg-white/40 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 text-center font-poppins">
                    Your payment is secure and processed by PayHere
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}